// React & React ecosystem
import { useNavigate } from 'react-router-dom';

// External libraries (alphabetically)
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// Internal absolute imports (@/ aliases)
// Stores
import { useAuthStore } from '@/store/authStore';

// Relative imports from same feature
import { authService } from '../services/authService';
import type { LoginRequest } from '../types';

/**
 * DOCU: React Query mutation hook for user login/authentication. <br>
 * Triggered: Called when user submits LoginForm. <br>
 * Last Updated Date: December 1, 2025 <br>
 * @returns Mutation object with login function, loading state, and error handling.
 * @author Kirt, Updated by: Sam
 *
 * @example
 * ```tsx
 * const { mutate: login, isPending } = useLogin();
 *
 * const handleSubmit = (data: LoginRequest) => {
 *   login(data);
 * };
 * ```
 */
export const useLogin = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation({
    mutationFn: (data: LoginRequest) => authService.login(data),
    onSuccess: (response) => {
      const { user, access_token, refresh_token, requires_otp, user_id, email_address, role_type_id, otp_challenge_token } = response.result;

      if (requires_otp) {
        if (!user_id || !email_address || !otp_challenge_token) {
          toast.error('Invalid OTP challenge. Please try again.');
          return;
        }

        sessionStorage.setItem('otp_pending_auth', JSON.stringify({
          user_id,
          email_address,
          role_type_id,
          otp_challenge_token,
        }));
        navigate('/otp');
        return;
      }

      if (!user || !access_token) {
        toast.error('Invalid login response. Please try again.');
        return;
      }

      // Set user in Zustand store (which persists to localStorage)
      // Pass refresh_token if available
      setUser(user, access_token, refresh_token);

      // Show success message
      toast.success(`Welcome back, ${user.first_name}!`);

      // Invalidate queries that depend on auth
      queryClient.invalidateQueries();

      // Small delay to ensure Zustand persist completes before navigation
      setTimeout(() => {
        navigate('/dashboard');
      }, 100);
    },
    onError: () => {
      // Error is handled inline in the form via the mutation's error state
    },
  });
};
